/**
 * Two pieces of audit-job UI:
 *
 * - <RunAuditButton/> — header button that navigates to the trigger-audit
 *   page (single canonical surface for kicking off a run). Was previously
 *   a modal; consolidated to one full-page experience so the trigger UI
 *   doesn't fork between two places.
 *
 * - <ActiveJobBanner/> — polls /api/audit-jobs/active every 3s and renders
 *   a sticky progress card with per-stage status. Disappears when no job
 *   is running. Used on the case queue + the trigger-audit page.
 */

import { useEffect, useState } from 'react';
import { Icon } from './Icon';
import { api } from '../lib/api';

interface JobStage {
  name: string;
  status: 'pending' | 'running' | 'complete' | 'failed' | 'skipped';
  started_at?: string;
  completed_at?: string;
  message?: string;
  count?: number;
}

interface AuditJob {
  id: number;
  scope_states: string[];
  scope_af_numbers: string[] | null;
  audit_window_days: number;
  sync_zoho: boolean;
  sync_axiscare: boolean;
  status: 'pending' | 'running' | 'complete' | 'failed' | 'cancelled';
  current_stage: string | null;
  stages: JobStage[];
  audit_run_id: number | null;
  error: string | null;
  created_at: string;
  started_at: string | null;
  completed_at: string | null;
}

const STAGE_LABELS: Record<string, string> = {
  sync_franchisees:   'Zoho · franchisees',
  sync_spc:           'Zoho · SPC / IR ledger',
  axiscare_evv:       'AxisCare · EVV (visits, clients, caregivers, GPS)',
  warehouse_client_status: 'Warehouse · client status, end date, start of care',
  axiscare_billing:   'AxisCare · billing (payer, rate, actual clock times)',
  audit_run:          'Audit · decision tree',
};

export function RunAuditButton({ onClick }: { onClick: () => void }) {
  return (
    <button className="btn" onClick={onClick}>
      <Icon name="play" size={13} /> Run audit
    </button>
  );
}

export function ActiveJobBanner({ refreshKey }: { refreshKey: number }) {
  const [job, setJob] = useState<AuditJob | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setHidden(false);
    const tick = async () => {
      try {
        const d = await api<{ job: AuditJob | null }>('/api/audit-jobs/active');
        if (cancelled) return;
        setJob(d.job);
      } catch { /* keep last state on transient error */ }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [refreshKey]);

  if (!job || hidden) return null;
  const stages: JobStage[] = job.stages ?? [];
  const overall = job.status === 'complete' ? 'complete' : job.status === 'failed' ? 'failed' : 'running';

  return (
    <div style={{
      marginTop: 12, padding: 12, background: 'var(--surface)',
      border: '1px solid var(--accent)', borderRadius: 8, boxShadow: 'var(--shadow-sm)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={`pill-dot dot-${overall === 'complete' ? 'ok' : overall === 'failed' ? 'danger' : 'warn'}`}></span>
          <strong style={{ fontSize: 13 }}>Audit job #{job.id}</strong>
          <span className="muted" style={{ fontSize: 12 }}>
            · {job.scope_states.join(', ')} · {job.audit_window_days}-day window
          </span>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => setHidden(true)} title="Hide banner">
          <Icon name="close" size={11} />
        </button>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {stages.map((s) => <StageRow key={s.name} stage={s} />)}
      </div>
      {job.error && (
        <div className="c-warn" style={{ marginTop: 8, fontSize: 12 }}>Error: {job.error}</div>
      )}
    </div>
  );
}

function StageRow({ stage }: { stage: JobStage }) {
  const dotClass = {
    pending: 'dot-info',
    running: 'dot-warn',
    complete: 'dot-ok',
    failed: 'dot-danger',
    skipped: 'dot-info',
  }[stage.status];
  const colorClass = stage.status === 'failed' ? 'c-warn'
    : stage.status === 'complete' ? 'c-ok'
    : stage.status === 'running' ? 'c-warn'
    : 'muted';
  const label = STAGE_LABELS[stage.name] ?? stage.name;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, padding: '3px 0' }}>
      <span className={`pill-dot ${dotClass}`}></span>
      <span style={{ minWidth: 280, fontWeight: stage.status === 'running' ? 500 : 400 }}>{label}</span>
      <span className={colorClass} style={{ fontSize: 12 }}>
        {stage.message ?? stage.status}
      </span>
    </div>
  );
}
